// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { color, maxLengthAttr, mergeWithCss, xMask } from "@pacific/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as a disabled textfield.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    // XNG-Changes
    showLabel?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        sizeVariant: "small",
        cssOverrides: {
            formField: css`
                width: auto;
            `,
            inputSelect: css`
                width: 43px;
                text-align: center;
                font-size: 14px;
                letter-spacing: 0;
                font-weight: 500;
                line-height: 19px;
                border: 1px solid ${color("common.ui3")};
                &,
                &:focus {
                    padding: 0;
                }
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    showLabel,
}) => {
    const localOnQtyOrderedChange = (value: string) => {
        // XNG-Changes
        const inputQty = Number(xMask.qty(value));
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    // XNG-Changes: apply qty mask for value
    const xBlurHandler = (event: any) => {
        event.currentTarget.value = Number(xMask.qty(event.currentTarget.value || "0"));
        changeHandler(event);
        setTimeout(() => {
            blurHandler();
        }, 500);
    };

    // XNG-Changes: apply qty mask for value
    const xChangeHandler = (event: any) => {
        event.target.value = Number(xMask.qty(event.target.value));
        changeHandler(event);
    };

    // XNG-Changes: mergeWithCss instead of mergeToNew
    const [styles] = React.useState(() => mergeWithCss(cartLineQuantityStyles, extendedStyles));

    if (editable && !isOutOfStock(cartLine)) {
        // XNG-Changes: input type
        return (
            <TextField
                {...styles.editableQuantityTextField}
                type="text"
                max={999999}
                maxLength={maxLengthAttr.qty}
                label={!showLabel ? undefined : label || translate("QTY")}
                value={value}
                disabled={!cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]}
                onChange={xChangeHandler}
                onKeyDown={keyDownHandler}
                onBlur={xBlurHandler}
                onClick={event => event.currentTarget.focus()}
                data-test-selector="cartline_qty"
            />
        );
    }

    // XNG-Changes: show disabled TextField
    return (
        <TextField
            {...styles.editableQuantityTextField}
            type="text"
            label={!showLabel ? undefined : label || translate("QTY")}
            value={value}
            disabled={true}
            data-test-selector="cartline_qty"
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
